import { ProblemDetails } from "../models/Http";
import { del, get, post, put, upload } from "./api";
import {
  Tenant,
  TenantWriteModel,
  TenantType,
  TenantDistributorManager,
  TenantEndUser,
  PaymentProvider
} from "../models/Tenant";
import { err, ok, Result } from "../result";
import { Distributor, Priority } from "../models/Product";
import { Language } from "../models/Translation";
import { ReceiptEmailTemplate } from "../models/EmailTemplate";
import { OnBoarding, Support } from "../models/Tenant";

const toWriteModel = (tenant: Tenant): TenantWriteModel => {
  return {
    name: tenant.name,
    companyName: tenant.companyName,
    contactName: tenant.contactName,
    contactEmail: tenant.contactEmail,
    orgNo: tenant.orgNo,
    address: tenant.address,
    city: tenant.city,
    postalCode: tenant.postalCode,
    managerIds: tenant.managers.map(x => x.id),
    externalPersonnelIds: tenant.externalPersonnel?.map(x => x.id),
    tenantType: tenant.tenantType,
    market: tenant.market,
    languageIds: tenant.languageIds,
    loginMethods: tenant.loginMethods,
    paymentProviders: tenant.paymentProviders,
    verificationMethods: tenant.verificationMethods,
    configuration: tenant.configuration,
    status: tenant.status,
    bic: tenant.bic,
    iban: tenant.iban
  };
};

export const getCurrentTenant = async (): Promise<
  Result<ProblemDetails, Tenant>
> => {
  const response = await get(`/v1/tenants/current`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as Tenant);
  }

  return err(content as ProblemDetails);
};

export const list = async (
  opened?: boolean
): Promise<Result<ProblemDetails, Tenant[]>> => {
  const response = await get(
    `/v1/tenants`,
    opened === undefined ? { opened } : {}
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as Tenant[]);
  }

  return err(content as ProblemDetails);
};

export const add = async (
  tenant: Tenant
): Promise<Result<ProblemDetails, string>> => {
  const response = await post("/v1/tenants", toWriteModel(tenant));
  const content = await response.json();

  if (!response.ok) {
    return err(content as ProblemDetails);
  }

  return ok(content);
};

export const update = async (
  tenant: Tenant
): Promise<Result<ProblemDetails, any>> => {
  const response = await put(`/v1/tenants/${tenant.id}`, toWriteModel(tenant));

  if (!response.ok) {
    const content = await response.json();
    return err(content as ProblemDetails);
  }

  return ok({});
};

export const endUserUpdateTenant = async (
  tenant: TenantEndUser
): Promise<Result<ProblemDetails, any>> => {
  const response = await put(`/v1/tenants/update`, tenant);

  if (!response.ok) {
    const content = await response.json();
    return err(content as ProblemDetails);
  }

  return ok({});
};

export const updateTenantStatus = async (
  status: string
): Promise<Result<ProblemDetails, any>> => {
  const response = await put(`/v1/tenants/update-status`, status);

  if (!response.ok) {
    const content = await response.json();
    return err(content as ProblemDetails);
  }

  return ok({});
};

export const addManagerToTenantUnique = async (
  tenantId: string
): Promise<Result<ProblemDetails, any>> => {
  const response = await put(`/v1/tenants/${tenantId}/manager`);

  if (!response.ok) {
    const content = await response.json();
    return err(content as ProblemDetails);
  }

  return ok({});
};

export const uploadImage = async (
  id: string,
  image: Blob,
  name: string
): Promise<Result<ProblemDetails, any>> => {
  const response = await upload(`/v1/tenants/${id}/${name}`, image, name);

  const content = await response.json();

  if (response.ok) {
    return ok(content);
  }

  return err(content as ProblemDetails);
};

export const listTenantTypes = async (): Promise<
  Result<ProblemDetails, ReadonlyArray<TenantType>>
> => {
  const response = await get(`/v1/tenants/types`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<TenantType>);
  }

  return err(content as ProblemDetails);
};

export const listTenantBrandNames = async (): Promise<
  Result<ProblemDetails, ReadonlyArray<string>>
> => {
  const response = await get(`/v1/tenants/brands`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<string>);
  }

  return err(content as ProblemDetails);
};

export const listTenantProductPriorities = async (): Promise<
  Result<ProblemDetails, ReadonlyArray<Priority>>
> => {
  const response = await get(`/v1/tenants/product-priorities`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<Priority>);
  }

  return err(content as ProblemDetails);
};

export const listTenantDistributors = async (): Promise<
  Result<ProblemDetails, ReadonlyArray<Distributor>>
> => {
  const response = await get(`/v1/tenants/distributors`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<Distributor>);
  }

  return err(content as ProblemDetails);
};

export const listDistributorsForTenantId = async (
  tenantId: string
): Promise<Result<ProblemDetails, ReadonlyArray<Distributor>>> => {
  const response = await get(`/v1/tenants/${tenantId}/distributors`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<Distributor>);
  }

  return err(content as ProblemDetails);
};

export const listTenantDistributorManagers = async (
  tenantId: string
): Promise<Result<ProblemDetails, ReadonlyArray<TenantDistributorManager>>> => {
  const response = await get(`/v1/tenants/${tenantId}/distributor-managers`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<TenantDistributorManager>);
  }

  return err(content as ProblemDetails);
};

export const addTenantDistributorManager = async (
  manager: TenantDistributorManager
): Promise<Result<ProblemDetails, any>> => {
  const response = await post(`/v1/tenants/distributor-manager`, manager);

  if (!response.ok) {
    const content = await response.json();
    return err(content as ProblemDetails);
  }

  return ok({});
};

export const deleteTenantDistributorManager = async (
  id: string
): Promise<Result<ProblemDetails, any>> => {
  const response = await del(`/v1/tenants/distributor-manager/${id}`);

  if (!response.ok) {
    const content = await response.json();
    return err(content as ProblemDetails);
  }

  return ok({});
};

export const getCurrentTenantLanguages = async (): Promise<
  Result<ProblemDetails, ReadonlyArray<Language>>
> => {
  const response = await get(`/v1/tenants/current/languages`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<Language>);
  }

  return err(content as ProblemDetails);
};

export const getPaymentProviders = async (): Promise<
  Result<ProblemDetails, ReadonlyArray<PaymentProvider>>
> => {
  const response = await get(`/v1/tenants/paymentproviders`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<PaymentProvider>);
  }

  return err(content as ProblemDetails);
};

export const getTenantLanguages = async (
  tenantId: string
): Promise<Result<ProblemDetails, ReadonlyArray<Language>>> => {
  const response = await get(`/v1/tenants/${tenantId}/languages`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<Language>);
  }

  return err(content as ProblemDetails);
};

export const getReceiptEmailTemplates = async (
  tenantId: string
): Promise<Result<ProblemDetails, ReadonlyArray<ReceiptEmailTemplate>>> => {
  const response = await get(`/v1/tenants/${tenantId}/receipt-email-templates`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<ReceiptEmailTemplate>);
  }

  return err(content as ProblemDetails);
};

export const saveReceiptEmailTemplates = async (
  tenantId: string,
  templates: ReceiptEmailTemplate[]
): Promise<Result<ProblemDetails, any>> => {
  const response = await put(
    `/v1/tenants/${tenantId}/receipt-email-templates`,
    templates
  );

  const content = await response.json();
  if (response.ok) {
    return ok(content as ReadonlyArray<ReceiptEmailTemplate>);
  }

  return err(content as ProblemDetails);
};

export const upsertSupport = async (
  id: string,
  support: Support
): Promise<Result<ProblemDetails, any>> => {
  const response = await put(`/v1/tenants/${id}/support`, support);

  if (!response.ok) {
    const content = await response.json();
    return err(content as ProblemDetails);
  }

  return ok({});
};

export const upsertOnBoardings = async (
  id: string,
  onBoardings: ReadonlyArray<OnBoarding>
): Promise<Result<ProblemDetails, any>> => {
  const response = await put(`/v1/tenants/${id}/onboarding`, onBoardings);

  if (!response.ok) {
    const content = await response.json();
    return err(content as ProblemDetails);
  }

  return ok({});
};

export const uploadOnBoardingImage = async (
  id: string,
  image: Blob
): Promise<Result<ProblemDetails, OnBoarding>> => {
  const response = await upload(`/v1/tenants/${id}/onboarding-image`, image);
  const content = await response.json();

  if (response.ok) {
    return ok(content as OnBoarding);
  }

  return err(content as ProblemDetails);
};
